import { useState, useEffect } from "react";
import { Paper, Tooltip } from "@mui/material";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  deleteInvoice,
  updateInvoiceStatus,
  addInvoiceComment,
  INVOICE_STATUSES,
  formatCurrency,
} from "./Api";
import { InvoiceFormModal } from "./InvoiceFormModal";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { InvoiceForwardModal } from "./InvoiceForwardModal";

const STATUS_LOOKUP = INVOICE_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const STATUS_VALUES = INVOICE_STATUSES.map((s) => s.value);

const isOverdue = (inv) =>
  inv.status === "Overdue" ||
  (inv.dueDate && inv.status !== "Paid" && new Date(inv.dueDate) < new Date());

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LOOKUP[status] || STATUS_LOOKUP.Unpaid;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem",
        fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const InlineStatusSelect = ({ invoice, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const cfg = STATUS_LOOKUP[invoice.status] || STATUS_LOOKUP.Unpaid;

  const handleChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === invoice.status) return;

    const res = await Swal.fire({
      title: `Change status to "${newStatus}"?`,
      input: "textarea",
      inputLabel: "Optional note (kept in status history)",
      inputPlaceholder: "Why are you changing this?",
      showCancelButton: true,
      confirmButtonText: "Update",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setLoading(true);
    try {
      const result = await updateInvoiceStatus(invoice._id, {
        status: newStatus,
        note: res.value || "",
      });
      onStatusChange(invoice._id, result?.data || { ...invoice, status: newStatus });
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <select
        value={invoice.status}
        onChange={handleChange}
        disabled={loading}
        onClick={(e) => e.stopPropagation()}
        style={{
          appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          borderRadius: 20, padding: "3px 28px 3px 12px", fontSize: "0.72rem",
          fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", outline: "none",
          whiteSpace: "nowrap", backgroundImage: "none", minWidth: 110, opacity: loading ? 0.7 : 1,
        }}
      >
        {STATUS_VALUES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <span style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
        {loading ? (
          <Spinner size="sm" style={{ color: cfg.color, width: 11, height: 11 }} />
        ) : (
          <svg width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L4.5 4L8 1" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </div>
  );
};

const InvoicesTable = ({
  invoices: initialInvoices,
  loading,
  properties,
  vendors,
  userRole,
  onRefresh,
  filters,
}) => {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardInvoice, setForwardInvoice] = useState(null);

  useEffect(() => { setInvoices(initialInvoices); }, [initialInvoices]);

  const canEdit = ["company", "manager", "technical staff", "vendor"].includes(
    (userRole || "").toLowerCase()
  );

  const canCreateNew = !!(filters?.propertyId && filters?.buildingName);

  const prefilledPropertyLabel = (() => {
    if (!filters?.propertyId) return "";
    const prop = (properties || []).find((p) => p._id === filters.propertyId);
    return prop ? (prop.title || prop.name || filters.propertyId) : filters.propertyId;
  })();

  const openNew = () => {
    if (!canCreateNew) {
      toast.warning("Select a property and building before creating a new invoice.");
      return;
    }
    if (!vendors || vendors.length === 0) {
      toast.warning("Add at least one vendor (Vendors tab) before creating an invoice.");
      return;
    }
    setSelected(null);
    setIsNew(true);
    setFormOpen(true);
  };

  const openEdit = (invoice, e) => {
    e?.stopPropagation?.();
    setSelected(invoice);
    setIsNew(false);
    setFormOpen(true);
  };

  const openDetail = (invoice) => {
    setDrawerInvoice(invoice);
    setDrawerOpen(true);
  };

  const closeForm = (refresh = true) => {
    setFormOpen(false);
    setSelected(null);
    if (refresh) onRefresh?.();
  };

  const openForward = (invoice, e) => {
    e.stopPropagation();
    setForwardInvoice(invoice);
    setForwardOpen(true);
  };

  const handleStatusChange = (id, updated) => {
    setInvoices((prev) => prev.map((r) => (r._id === id ? updated : r)));
    if (drawerInvoice && drawerInvoice._id === id) setDrawerInvoice(updated);
  };

  const handleInlineComment = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Add a comment",
      input: "textarea",
      inputLabel: "Your comment is saved with your name, role and time",
      inputPlaceholder: "Type a note for the team…",
      showCancelButton: true,
      confirmButtonText: "Post",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) => (!v?.trim() ? "Comment cannot be empty" : undefined),
    });
    if (!res.isConfirmed) return;
    try {
      const result = await addInvoiceComment(invoice._id, { comment: res.value.trim() });
      const newHistory = result?.data?.commentsHistory;
      if (newHistory) {
        const updated = { ...invoice, commentsHistory: newHistory };
        setInvoices((prev) => prev.map((r) => (r._id === invoice._id ? updated : r)));
        if (drawerInvoice && drawerInvoice._id === invoice._id) setDrawerInvoice(updated);
      }
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  const handleDelete = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Delete Invoice?",
      html: `<p>Delete invoice <strong>${invoice.title || `#${invoice.invoiceNo}`}</strong>?<br/><small style="color:#64748B">This cannot be undone.</small></p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInvoice(invoice._id);
      onRefresh?.();
    } catch {
      toast.error("Failed to delete invoice");
    }
  };

  const colSpan = canEdit ? 11 : 10;
  const fmt = (d) => (d ? moment(d).format("MMM D, YYYY") : "—");

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="file-text" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Finance</div>
              <div className="iv-header__title">Invoices</div>
            </div>
            {invoices.length > 0 && (
              <div className="iv-header__badges">
                <span className="iv-header__badge iv-header__badge--records">
                  {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
          <div className="iv-header__actions">
            {canEdit && (
              <Tooltip title={canCreateNew ? "" : "Select a property and building to create a new invoice"} arrow>
                <span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--primary"
                    onClick={openNew}
                    disabled={!canCreateNew}
                    style={{ opacity: canCreateNew ? 1 : 0.5, cursor: canCreateNew ? "pointer" : "not-allowed" }}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    New Invoice
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="iv-tbl" style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th style={{ minWidth: 120 }}>Documented On</th>
                <th style={{ minWidth: 110, textAlign: "left", paddingLeft: 10 }}>Reference</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                <th style={{ minWidth: 130 }}>Category</th>
                <th style={{ minWidth: 110, textAlign: "right" }}>Amount</th>
                <th style={{ minWidth: 140 }}>Status</th>
                <th style={{ minWidth: 110 }}>Comments</th>
                <th style={{ minWidth: 70 }}>Files</th>
                {canEdit && <th style={{ width: 120 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spinner style={{ color: "#4F46E5" }} />
                  </td>
                </tr>
              )}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="iv-empty-cell">
                    <div className="iv-empty" style={{ minHeight: "25vh" }}>
                      <div className="iv-empty__icon-wrap">
                        <FeatherIcon icon="file-text" size={24} color="#4F46E5" />
                      </div>
                      <div className="iv-empty__title">No Invoices Yet</div>
                      <div className="iv-empty__sub">
                        {canEdit
                          ? canCreateNew
                            ? 'Click "New Invoice" to upload your first invoice.'
                            : "Select a property and building, then click \"New Invoice\" to begin."
                          : "No invoices found for the current filters."}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && invoices.map((inv) => {
                const categoryName = inv.category?.name || "—";
                const propertyName = inv.propertyTitle || inv.property?.title || inv.property?.name || "";
                const attachmentCount = inv.attachments?.length || 0;
                const commentsCount = inv.commentsHistory?.length || 0;
                const overdue = isOverdue(inv);

                return (
                  <tr
                    key={inv._id}
                    className="iv-row"
                    onClick={() => openDetail(inv)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: "#4F46E5", fontSize: "0.82rem" }}>
                        {inv.invoiceNo ? `#${inv.invoiceNo}` : "—"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        fontSize: "0.78rem",
                        color: overdue ? "#B91C1C" : "#475569",
                        fontWeight: overdue ? 700 : 500,
                      }}>
                        {fmt(inv.invoiceDate)}{overdue ? " ⚠" : ""}
                      </span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <span style={{ fontSize: "0.8rem", color: inv.referenceNo ? "#0F172A" : "#CBD5E1" }}>
                        {inv.referenceNo || "—"}
                      </span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>
                        {inv.vendorName || "—"}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                        {inv.title || "Untitled"}
                      </div>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>{propertyName || "—"}</div>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 1 }}>{inv.property?.buildingName || "—"}</div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", background: "#EEF2FF", color: "#4338CA",
                        border: "1px solid #C7D2FE", borderRadius: 6,
                        padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
                      }}>
                        {categoryName}
                      </span>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      {formatCurrency(inv.amount, inv.currency)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      {canEdit ? (
                        <InlineStatusSelect invoice={inv} onStatusChange={handleStatusChange} />
                      ) : (
                        <StatusBadge status={inv.status} />
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {commentsCount > 0 && (
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <div style={{ maxWidth: 280, padding: "4px 2px", maxHeight: 260, overflowY: "auto" }}>
                                {(inv.commentsHistory || [])
                                  .slice()
                                  .reverse()
                                  .map((c, i) => (
                                    <div
                                      key={c._id || i}
                                      style={{
                                        paddingBottom: 7, marginBottom: 7,
                                        borderBottom: i < commentsCount - 1 ? "1px solid rgba(255,255,255,0.18)" : "none",
                                      }}
                                    >
                                      <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                                        {c.author?.displayName || "Unknown"}
                                        {c.author?.role ? (
                                          <span style={{ opacity: 0.75, fontWeight: 500 }}> · {c.author.role}</span>
                                        ) : null}
                                      </div>
                                      <div style={{ fontSize: "0.62rem", opacity: 0.7, marginBottom: 2 }}>
                                        {c.createdAt ? moment(c.createdAt).format("MMM D, YYYY · h:mm A") : ""}
                                      </div>
                                      <div style={{ fontSize: "0.72rem", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                                        {c.comment}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            }
                          >
                            <span className="iv-chip iv-chip--gray" style={{ cursor: "help" }}>
                              <FeatherIcon icon="message-square" size={11} />{commentsCount}
                            </span>
                          </Tooltip>
                        )}
                        {canEdit ? (
                          <Tooltip title="Add comment" arrow>
                            <button
                              type="button"
                              className="iv-ebtn"
                              onClick={(e) => handleInlineComment(inv, e)}
                            >
                              <FeatherIcon icon="plus" size={12} color="#4F46E5" />
                            </button>
                          </Tooltip>
                        ) : (
                          commentsCount === 0 && <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {attachmentCount > 0 ? (
                        <span className="iv-chip iv-chip--indigo">
                          <FeatherIcon icon="paperclip" size={11} />{attachmentCount}
                        </span>
                      ) : (
                        <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                        <div className="iv-action-group">
                          <Tooltip title="View Details" arrow>
                            <button type="button" className="iv-ebtn" onClick={() => openDetail(inv)}>
                              <FeatherIcon icon="eye" size={13} color="#0284C7" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <button type="button" className="iv-ebtn" onClick={(e) => openEdit(inv, e)}>
                              <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Forward" arrow>
                            <button
                              type="button"
                              className="iv-ebtn"
                              style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                              onClick={(e) => openForward(inv, e)}
                            >
                              <FeatherIcon icon="send" size={13} color="#059669" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <button type="button" className="iv-dbtn" onClick={(e) => handleDelete(inv, e)}>
                              <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      <InvoiceFormModal
        isOpen={formOpen}
        onClose={closeForm}
        invoiceData={selected}
        isNew={isNew}
        properties={properties}
        vendors={vendors}
        prefilledPropertyId={filters?.propertyId || ""}
        prefilledBuildingName={filters?.buildingName || ""}
        prefilledPropertyLabel={prefilledPropertyLabel}
      />

      <InvoiceDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerInvoice(null); }}
        invoice={drawerInvoice}
        canEdit={canEdit}
        onChange={(updated) => {
          setDrawerInvoice(updated);
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
        }}
        onForward={() => { setForwardInvoice(drawerInvoice); setForwardOpen(true); }}
        onEdit={() => {
          setSelected(drawerInvoice);
          setIsNew(false);
          setFormOpen(true);
        }}
      />

      <InvoiceForwardModal
        isOpen={forwardOpen}
        onClose={() => { setForwardOpen(false); setForwardInvoice(null); }}
        invoice={forwardInvoice}
        onForwardSuccess={(updatedLog) => {
          if (!forwardInvoice) return;
          const updated = { ...forwardInvoice, communicationLog: updatedLog };
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
          if (drawerInvoice && drawerInvoice._id === updated._id) setDrawerInvoice(updated);
        }}
      />
    </>
  );
};

export { InvoicesTable };
